import {teamSectionStyle} from '@/pages/Organization/Profile/Team/Team.styles';
import TeamFilterContent from '@/shared/components/Table/FilterMenu/FilterContent';
import Input from '@/shared/components/Inputs/Input';
import FilterMenu from '@/shared/components/Table/FilterMenu';
import {ReactComponent as SearchIcon} from '@icons/search-normal.svg';
import organization from '@images/organization-example.png';
import {BODY2, LAST_ACTIVE, MEMBER, ROLE, SUBTITLE1, SUBTITLE2, DD_MM_YYYY_HH_MM_DOT} from '@/utils/constants';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {useEffect, useState} from 'react';
import Loader from '@/shared/components/Loader';
import {FilterActivity, FilterRole, IFilterGroup} from '@/utils/types';
import {defaultFilterOptions} from '@/pages/Organization/Profile/Team/helpers';
import {useAppDispatch} from '@/store/hooks';
import {useSelector} from 'react-redux';
import {getTeamDataSelector, getTeamMembers} from '@/store/orgatization-team';
import Pagination from '@/shared/components/Pagination';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import dayjs from 'dayjs';
import {useApiInstance} from '@/contexts/ApiInstanceContext';

const TeamMembersInfo = ({id}: {id: number | null}) => {
  const dispatch = useAppDispatch();

  const teamMembers = useSelector(getTeamDataSelector);

  const [searchValue, setSearchValue] = useState('');
  const [role, setRole] = useState(defaultFilterOptions[0].options[0].value);
  const [activity, setActivity] = useState(defaultFilterOptions[1].options[0].value);
  const [filterData, setFilterData] = useState<IFilterGroup[]>(defaultFilterOptions);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);

  const pageSize = 9;
  const totalCount = teamMembers.totalCount ?? 0;

  const {apiInstance} = useApiInstance();

  const handleFilterChange = (groupTitle: string, optionTitle: string) => {
    if (groupTitle === 'Role') setRole(optionTitle);
    else setActivity(optionTitle);
  };
  const getLastActiveSx = (lastActive: string) => {
    if (lastActive.includes('Active')) return teamSectionStyle.lastActiveActive;
    if (lastActive.includes('Invited')) return teamSectionStyle.lastActiveInvited;
    return teamSectionStyle.lastActiveDate;
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    setPage(1);
  };

  const getTeamList = async () => {
    setIsLoading(true);

    const memberDto = {
      page,
      pageSize,
      searchName: searchValue || '',
      role: role! as FilterRole,
      activity: activity! as FilterActivity,
      organizationId: id!,
    };

    try {
      await dispatch(getTeamMembers({reqBody: memberDto, apiInstance})).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!id) return;

    getTeamList();
  }, [activity, role, page, searchValue, id]);

  return (
    <Box sx={teamSectionStyle.wrapper}>
      <Box sx={teamSectionStyle.tableWrapper}>
        <Box sx={teamSectionStyle.filterRow}>
          <Box>
            <FilterMenu content={<TeamFilterContent options={filterData} onFilterChange={handleFilterChange} />} />
          </Box>
          <Box sx={teamSectionStyle.search}>
            <SearchIcon />
            <Input placeholder="Search team members" value={searchValue} onChange={handleSearch} />
          </Box>
        </Box>
        {isLoading ? (
          <Loader sx={teamSectionStyle.loader} />
        ) : teamMembers.data.length === 0 ? (
          <EmptyTableData />
        ) : (
          <Box sx={teamSectionStyle.tableWrapper}>
            <TableContainer sx={teamSectionStyle.tableContainer}>
              <Table sx={{minWidth: 650}} aria-label="simple table">
                <TableHead>
                  <TableRow sx={{height: '32px'}}>
                    <TableCell sx={teamSectionStyle.avatarHeadRow} />
                    <TableCell> {MEMBER}</TableCell>
                    <TableCell sx={teamSectionStyle.lastActiveHeadRow}>{LAST_ACTIVE}</TableCell>
                    <TableCell sx={teamSectionStyle.roleHeadRow}>{ROLE}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>

                <TableBody>
                  {teamMembers.data.length &&
                    teamMembers.data.map((row) => (
                      <TableRow sx={teamSectionStyle.avatarBodyRow} key={row.id}>
                        <TableCell>
                          <Box>
                            <IconButton>
                              <img
                                src={row.logo || organization}
                                alt="avatar"
                                style={{borderRadius: '50%', width: '44px', height: '44px'}}
                              />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant={SUBTITLE1} sx={teamSectionStyle.name}>
                              {`${row.firstName} ${row.lastName}`}
                            </Typography>
                          </Box>
                          <Typography variant={BODY2} sx={teamSectionStyle.email}>
                            {row.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={getLastActiveSx(row.status)}>
                            {row.lastActive
                              ? dayjs.utc(row.lastActive).local().format(DD_MM_YYYY_HH_MM_DOT)
                              : 'Invitation sent'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={teamSectionStyle.roleBodyRow}>
                          <Typography variant={SUBTITLE2} sx={teamSectionStyle.owner}>
                            {row.role}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={teamSectionStyle.pagination}>
              <Pagination page={page} pageSize={pageSize} total={totalCount} onChange={(step) => setPage(step)} />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TeamMembersInfo;
